// The looks of the §7.3 particle cosmetics: the 6 trails (shown during lunge, flight and fall) and
// the 4 KO celebrations (played over the victim's splash point). Small instanced 3D shapes in the
// toy-box style (one draw per shape in use), recipes keyed by catalog `key`. The Locker turntable
// and the `?debug=cosmetics` sheet use them directly; in game WP7's particle pools own spawning.
import {
  Color, DoubleSide, DynamicDrawUsage, ExtrudeGeometry, Group, IcosahedronGeometry, InstancedMesh, Matrix4, Mesh,
  MeshBasicMaterial, MeshStandardMaterial, MeshToonMaterial, PlaneGeometry, Quaternion, Shape, SphereGeometry,
  Vector2, Vector3, type BufferGeometry, type Material, type Texture,
} from 'three';
import { starPoints } from './geo';

type Shape3 = 'sparkle' | 'bubble' | 'heart' | 'note' | 'ribbon' | 'confetti' | 'spark' | 'petal' | 'flower' | 'glint';

const MAX_PER_SHAPE = 96;
const RAINBOW = ['#FF5A4E', '#FF9A3C', '#FFC93C', '#3DDC97', '#4CB8FF', '#9B6BFF'];
const CONFETTI = ['#FF5A4E', '#FFC93C', '#3DDC97', '#4CB8FF', '#9B6BFF', '#FF7AC8', '#FFFFFF'];
const FLOWERS = ['#FF7AC8', '#FFFFFF', '#FFB3C7', '#C7B8FF', '#FFC93C'];

const geoCache = new Map<Shape3, BufferGeometry>();

function extrudeShape(points: [number, number][], depth: number): ExtrudeGeometry {
  const s = new Shape(points.map(([x, y]) => new Vector2(x, y)));
  const g = new ExtrudeGeometry(s, { depth, bevelEnabled: true, bevelSize: depth * 0.4, bevelThickness: depth * 0.4, bevelSegments: 1, curveSegments: 6 });
  g.translate(0, 0, -depth / 2);
  g.computeVertexNormals();
  return g;
}

function heartPoints(): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i < 24; i++) {
    const t = (i / 24) * Math.PI * 2;
    const x = 16 * Math.pow(Math.sin(t), 3);
    const y = 13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t);
    out.push([x / 17, y / 17]);
  }
  return out;
}

function geometry(shape: Shape3): BufferGeometry {
  let g = geoCache.get(shape);
  if (g) return g;
  switch (shape) {
    case 'sparkle':
    case 'glint':
      g = extrudeShape(starPoints(1, 0.32, 4), 0.2);
      break;
    case 'spark':
      g = new IcosahedronGeometry(1, 0);
      break;
    case 'bubble':
      g = new SphereGeometry(1, 14, 10);
      break;
    case 'heart':
      g = extrudeShape(heartPoints(), 0.35);
      break;
    case 'note': {
      // An eighth note: head + stem + flag, as one extruded outline.
      const pts: [number, number][] = [];
      for (let i = 0; i <= 12; i++) {
        const a = Math.PI * 0.15 + (i / 12) * Math.PI * 2 * 0.9;
        pts.push([-0.25 + Math.cos(a) * 0.38, -0.55 + Math.sin(a) * 0.28]);
      }
      pts.push([0.1, -0.5], [0.1, 0.75], [0.55, 0.45], [0.5, 0.3], [0.22, 0.48], [0.22, -0.55]);
      g = extrudeShape(pts.reverse(), 0.22);
      break;
    }
    case 'ribbon':
    case 'confetti':
      g = new PlaneGeometry(1, 1);
      break;
    case 'petal':
      g = new SphereGeometry(1, 10, 6);
      g.scale(1, 0.25, 0.55);
      break;
    case 'flower':
      g = extrudeShape(starPoints(1, 0.55, 5), 0.25);
      break;
  }
  geoCache.set(shape, g);
  return g;
}

interface Pool {
  mesh: InstancedMesh;
  n: number;
  pos: Float32Array;
  vel: Float32Array;
  rot: Float32Array;
  spin: Float32Array;
  age: Float32Array;
  life: Float32Array;
  size: Float32Array;
  grav: Float32Array;
  drag: Float32Array;
}

const m4 = new Matrix4();
const q = new Quaternion();
const v = new Vector3();
const s3 = new Vector3();
const col = new Color();
const axis = new Vector3();

export type FxKey = 'sparkles' | 'bubbles' | 'hearts' | 'musicNotes' | 'rainbow' | 'confettiCannon' | 'fireworks' | 'flowerBurst' | 'discoBall';

/** One cosmetic effect instance (a trail or a celebration). */
export class CosmeticFx {
  readonly root = new Group();
  private readonly pools = new Map<Shape3, Pool>();
  private readonly poolList: Pool[] = [];
  private readonly mats: Material[] = [];
  private readonly main = new Color('#FF5A4E');
  private emitAcc = 0;
  private time = 0;
  private disco: Mesh | null = null;
  private discoAge = 99;
  private seed = 1;
  private ribbonLen = 0.1;
  /** Seconds the next spawned particle has already lived (sub-frame emission). */
  private preAge = 0;
  private ribbonYaw = 0;

  constructor(readonly key: FxKey, gradientMap: Texture) {
    this.root.name = `fx:${key}`;
    const lit = (): Material => {
      const m = new MeshToonMaterial({ color: '#FFFFFF', gradientMap, side: DoubleSide });
      this.mats.push(m);
      return m;
    };
    const glow = (): Material => {
      const m = new MeshBasicMaterial({ color: '#FFFFFF', toneMapped: false, side: DoubleSide });
      this.mats.push(m);
      return m;
    };
    const glass = (): Material => {
      const m = new MeshToonMaterial({ color: '#FFFFFF', gradientMap, transparent: true, opacity: 0.55, depthWrite: false });
      this.mats.push(m);
      return m;
    };
    const use = (shape: Shape3, mat: Material): void => {
      const mesh = new InstancedMesh(geometry(shape), mat, MAX_PER_SHAPE);
      mesh.instanceMatrix.setUsage(DynamicDrawUsage);
      mesh.count = 0;
      mesh.frustumCulled = false;
      for (let i = 0; i < MAX_PER_SHAPE; i++) mesh.setColorAt(i, col.set('#FFFFFF'));
      mesh.instanceColor?.setUsage(DynamicDrawUsage);
      this.root.add(mesh);
      const n = MAX_PER_SHAPE;
      const pool: Pool = {
        mesh, n: 0, pos: new Float32Array(n * 3), vel: new Float32Array(n * 3), rot: new Float32Array(n * 3),
        spin: new Float32Array(n * 3), age: new Float32Array(n), life: new Float32Array(n), size: new Float32Array(n),
        grav: new Float32Array(n), drag: new Float32Array(n),
      };
      this.pools.set(shape, pool);
      this.poolList.push(pool);
    };
    switch (key) {
      case 'sparkles': use('sparkle', glow()); break;
      case 'bubbles': use('bubble', glass()); break;
      case 'hearts': use('heart', lit()); break;
      case 'musicNotes': use('note', lit()); break;
      case 'rainbow': use('ribbon', glow()); break;
      case 'confettiCannon': use('confetti', lit()); use('sparkle', glow()); break;
      case 'fireworks': use('spark', glow()); use('sparkle', glow()); break;
      case 'flowerBurst': use('petal', lit()); use('flower', lit()); break;
      case 'discoBall': {
        use('glint', glow());
        const ball = new Mesh(new IcosahedronGeometry(0.42, 2), new MeshStandardMaterial({ color: '#E8EEF4', metalness: 1, roughness: 0.12, flatShading: true }));
        this.mats.push(ball.material as Material);
        ball.visible = false;
        this.root.add(ball);
        this.disco = ball;
        break;
      }
    }
  }

  /** The owner's identity color (§7.2: celebrations play in the credited bean's identity color). */
  setColor(hex: string): void {
    this.main.set(hex);
  }

  private rand(): number {
    this.seed = (this.seed * 16807) % 2147483647;
    return (this.seed - 1) / 2147483646;
  }

  private spawn(shape: Shape3, x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, size: number, grav: number, drag: number, color: Color): void {
    const p = this.pools.get(shape);
    if (!p) return;
    let i = p.n;
    if (i >= MAX_PER_SHAPE) {
      // Recycle the oldest.
      let oldest = 0;
      for (let k = 1; k < MAX_PER_SHAPE; k++) if (p.age[k] / p.life[k] > p.age[oldest] / p.life[oldest]) oldest = k;
      i = oldest;
    } else p.n++;
    p.pos[i * 3] = x; p.pos[i * 3 + 1] = y; p.pos[i * 3 + 2] = z;
    p.vel[i * 3] = vx; p.vel[i * 3 + 1] = vy; p.vel[i * 3 + 2] = vz;
    p.rot[i * 3] = this.rand() * 6.28; p.rot[i * 3 + 1] = this.rand() * 6.28; p.rot[i * 3 + 2] = this.rand() * 6.28;
    p.spin[i * 3] = (this.rand() - 0.5) * 8; p.spin[i * 3 + 1] = (this.rand() - 0.5) * 8; p.spin[i * 3 + 2] = (this.rand() - 0.5) * 8;
    const pre = Math.min(this.preAge, life * 0.5);
    p.pos[i * 3] += vx * pre;
    p.pos[i * 3 + 1] += vy * pre;
    p.pos[i * 3 + 2] += vz * pre;
    p.age[i] = pre;
    p.life[i] = life;
    p.size[i] = size;
    p.grav[i] = grav;
    p.drag[i] = drag;
    p.mesh.setColorAt(i, color);
    if (p.mesh.instanceColor) p.mesh.instanceColor.needsUpdate = true;
  }

  /**
   * Trail emission from a moving emitter (world position, velocity m/s). `rate` scales the
   * emission (1 = the full trail during a lunge or flight). `treadmill`: the emitter stands still
   * (turntables), so ribbons stream backward instead of staying where they were laid.
   */
  trail(x: number, y: number, z: number, vx: number, vy: number, vz: number, dt: number, rate = 1, treadmill = false): void {
    const perSec = rate * (this.key === 'rainbow' ? 22 : 18);
    this.emitAcc += dt * perSec;
    while (this.emitAcc >= 1) {
      this.emitAcc -= 1;
      // Emissions are spread over the frame: older ones start older (and further along), so slow
      // frames do not stack a burst of particles in one spot.
      this.preAge = perSec > 0 ? this.emitAcc / perSec : 0;
      const r = (): number => this.rand() - 0.5;
      const back = 0.25;
      switch (this.key) {
        case 'sparkles':
          this.spawn('sparkle', x + r() * 0.6, y + 0.3 + this.rand() * 0.7, z + r() * 0.6, -vx * back + r(), 0.6 + this.rand(), -vz * back + r(), 0.6, 0.07 + this.rand() * 0.06, -1, 1.5, col.set(this.rand() < 0.5 ? '#FFF1A8' : '#FFFFFF'));
          break;
        case 'bubbles':
          this.spawn('bubble', x + r() * 0.5, y + 0.2 + this.rand() * 0.6, z + r() * 0.5, -vx * back + r() * 0.4, 0.8 + this.rand() * 0.6, -vz * back + r() * 0.4, 1.1, 0.05 + this.rand() * 0.08, -0.5, 1.2, col.set(this.rand() < 0.5 ? '#CFEFFF' : '#E8F8FF'));
          break;
        case 'hearts':
          this.spawn('heart', x + r() * 0.5, y + 0.4 + this.rand() * 0.6, z + r() * 0.5, -vx * back + r() * 0.5, 0.9 + this.rand() * 0.5, -vz * back + r() * 0.5, 0.9, 0.09 + this.rand() * 0.05, -0.4, 1.4, col.set(this.rand() < 0.65 ? '#FF4F9A' : '#FF8FB8'));
          break;
        case 'musicNotes':
          this.spawn('note', x + r() * 0.5, y + 0.5 + this.rand() * 0.6, z + r() * 0.5, -vx * back + r() * 0.5, 1 + this.rand() * 0.5, -vz * back + r() * 0.5, 1.0, 0.11 + this.rand() * 0.04, -0.3, 1.2, col.set(RAINBOW[Math.floor(this.rand() * RAINBOW.length)]));
          break;
        case 'rainbow': {
          // Six stacked ribbon bands laid along the path.
          // Six stacked bands; each segment is stretched to cover the gap to the next one.
          const sp = Math.hypot(vx, vz);
          const len = Math.max(0.08, (sp / (22 * Math.max(0.2, rate))) * 1.25);
          for (let b = 0; b < 6; b++) {
            const drift = treadmill ? -0.9 : 0;
            this.spawn('ribbon', x - vx * 0.02, y + 0.35 + (5 - b) * 0.07, z - vz * 0.02, vx * drift, 0, vz * drift, 0.6, 0.07, 0, 0, col.set(RAINBOW[b]));
            this.ribbonLen = len;
            this.ribbonYaw = Math.atan2(-vz, vx);
          }
          break;
        }
        default:
          break;
      }
    }
  }

  /** A celebration burst at (x, y, z) (§7.3: confetti cannon, fireworks, flower burst, disco ball). */
  burst(x: number, y: number, z: number): void {
    this.preAge = 0;
    const r = (): number => this.rand() - 0.5;
    switch (this.key) {
      case 'confettiCannon':
        for (let i = 0; i < 80; i++) {
          const a = this.rand() * Math.PI * 2;
          const sp = 1.2 + this.rand() * 2.2;
          const c = i % 3 === 0 ? this.main : col.set(CONFETTI[Math.floor(this.rand() * CONFETTI.length)]);
          this.spawn('confetti', x, y, z, Math.cos(a) * sp * 0.6, 4 + this.rand() * 3, Math.sin(a) * sp * 0.6, 2.2, 0.07 + this.rand() * 0.04, -6, 2.4, c);
        }
        for (let i = 0; i < 12; i++) this.spawn('sparkle', x + r() * 0.4, y + 0.3, z + r() * 0.4, r() * 3, 3 + this.rand() * 2, r() * 3, 0.6, 0.08, -4, 2, col.set('#FFF1A8'));
        break;
      case 'fireworks':
        for (let shell = 0; shell < 3; shell++) {
          const cx = x + (shell - 1) * 0.55;
          const cy = y + 1.2 + this.rand() * 0.5;
          const c = shell === 1 ? this.main : col.set(RAINBOW[Math.floor(this.rand() * RAINBOW.length)]);
          for (let i = 0; i < 24; i++) {
            const a = (i / 24) * Math.PI * 2;
            const e = r() * 0.8;
            const sp = 1.7 + this.rand() * 0.5;
            // Alternate round sparks and little four-point stars so the shell glitters.
            this.spawn(i % 3 === 0 ? 'sparkle' : 'spark', cx, cy, z, Math.cos(a) * sp, Math.sin(a) * sp * 0.85 + e, r() * 0.6, 1.1, i % 3 === 0 ? 0.09 : 0.055, -2.2, 1.5, c);
          }
          this.spawn('sparkle', cx, cy, z, 0, 0, 0, 0.4, 0.35, 0, 0, col.set('#FFFFFF'));
        }
        break;
      case 'flowerBurst':
        for (let i = 0; i < 46; i++) {
          const a = this.rand() * Math.PI * 2;
          const sp = 1 + this.rand() * 1.8;
          this.spawn('petal', x, y + 0.2, z, Math.cos(a) * sp, 2.5 + this.rand() * 2.2, Math.sin(a) * sp * 0.5, 2.0, 0.08 + this.rand() * 0.04, -3, 2.2, col.set(FLOWERS[Math.floor(this.rand() * FLOWERS.length)]));
        }
        for (let i = 0; i < 7; i++) {
          const a = (i / 7) * Math.PI * 2;
          this.spawn('flower', x, y + 0.3, z, Math.cos(a) * 1.4, 3 + this.rand(), Math.sin(a) * 0.6, 1.8, 0.13, -4, 1.5, i % 2 ? this.main : col.set('#FFC93C'));
        }
        break;
      case 'discoBall':
        this.discoAge = 0;
        if (this.disco) this.disco.position.set(x, y + 1.4, z);
        break;
      default:
        break;
    }
  }

  update(dt: number): void {
    this.time += dt;
    for (let pi = 0; pi < this.poolList.length; pi++) {
      const p = this.poolList[pi];
      let w = 0;
      for (let i = 0; i < p.n; i++) {
        p.age[i] += dt;
        if (p.age[i] >= p.life[i]) continue;
        // Compact live particles toward the front.
        if (w !== i) {
          for (let k = 0; k < 3; k++) {
            p.pos[w * 3 + k] = p.pos[i * 3 + k];
            p.vel[w * 3 + k] = p.vel[i * 3 + k];
            p.rot[w * 3 + k] = p.rot[i * 3 + k];
            p.spin[w * 3 + k] = p.spin[i * 3 + k];
          }
          p.age[w] = p.age[i];
          p.life[w] = p.life[i];
          p.size[w] = p.size[i];
          p.grav[w] = p.grav[i];
          p.drag[w] = p.drag[i];
          p.mesh.getColorAt(i, col);
          p.mesh.setColorAt(w, col);
        }
        const d = Math.exp(-p.drag[w] * dt);
        p.vel[w * 3] *= d;
        p.vel[w * 3 + 1] = p.vel[w * 3 + 1] * d + p.grav[w] * dt;
        p.vel[w * 3 + 2] *= d;
        for (let k = 0; k < 3; k++) {
          p.pos[w * 3 + k] += p.vel[w * 3 + k] * dt;
          p.rot[w * 3 + k] += p.spin[w * 3 + k] * dt;
        }
        const k01 = p.age[w] / p.life[w];
        // Pop in, hold, shrink out.
        const sc = p.size[w] * Math.min(1, k01 * 8) * (1 - Math.pow(k01, 3));
        v.set(p.pos[w * 3], p.pos[w * 3 + 1], p.pos[w * 3 + 2]);
        axis.set(p.rot[w * 3], p.rot[w * 3 + 1], p.rot[w * 3 + 2]);
        const flat = p.mesh.geometry === geometry('ribbon');
        if (flat) q.setFromAxisAngle(axis.set(0, 1, 0), this.ribbonYaw);
        else {
          const ang = axis.length();
          if (ang > 1e-6) q.setFromAxisAngle(axis.multiplyScalar(1 / ang), ang);
          else q.identity();
        }
        if (flat) s3.set(this.ribbonLen * Math.min(1, k01 * 8) * (1 - Math.pow(k01, 3)), sc, sc);
        else s3.set(sc, sc, sc);
        m4.compose(v, q, s3);
        p.mesh.setMatrixAt(w, m4);
        w++;
      }
      p.n = w;
      p.mesh.count = w;
      p.mesh.instanceMatrix.needsUpdate = true;
      if (p.mesh.instanceColor) p.mesh.instanceColor.needsUpdate = true;
    }
    if (this.disco) {
      this.discoAge += dt;
      const on = this.discoAge < 2.4;
      this.disco.visible = on;
      if (on) {
        const k = this.discoAge;
        const drop = Math.min(1, k * 4);
        this.disco.rotation.y += dt * 2.5;
        this.disco.scale.setScalar(drop * (1 - Math.max(0, k - 2.1) / 0.3));
        if (Math.floor(k * 30) !== Math.floor((k - dt) * 30)) {
          const a = this.rand() * Math.PI * 2;
          const rr = 0.5 + this.rand() * 1.2;
          const c = this.rand() < 0.4 ? this.main : col.set(RAINBOW[Math.floor(this.rand() * RAINBOW.length)]);
          this.spawn('glint', this.disco.position.x + Math.cos(a) * rr, this.disco.position.y - 0.4 - this.rand() * 1.2, this.disco.position.z + Math.sin(a) * rr * 0.4, 0, 0, 0, 0.35, 0.09, 0, 0, c);
        }
      }
    }
  }

  clear(): void {
    for (const p of this.poolList) {
      p.n = 0;
      p.mesh.count = 0;
    }
    this.discoAge = 99;
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    this.root.removeFromParent();
  }
}

/** Which effect recipe a trail / celebration catalog key uses (null = no effect, e.g. trail "none"). */
export function fxKey(key: string): FxKey | null {
  switch (key) {
    case 'sparkles': case 'bubbles': case 'hearts': case 'musicNotes': case 'rainbow':
    case 'confettiCannon': case 'fireworks': case 'flowerBurst': case 'discoBall':
      return key;
    default:
      return null;
  }
}

/** The comic word a honk shows on the turntable (§7.3 honks; the sound itself is WP8's). */
export const HONK_WORDS: Readonly<Record<string, string>> = {
  clownHorn: 'HONK!', duck: 'QUACK!', kazoo: 'BZZZT!', foghorn: 'BWOOOM!', bikeBell: 'DING-DING!', squeaky: 'SQUEAK!', goose: 'HONK!!', airhorn: 'BWAAAP!',
};

/** The comic word a fall scream shows on the turntable (§7.3 fall screams). */
export const SCREAM_WORDS: Readonly<Record<string, string>> = {
  slideWhistle: 'wheeeee~', opera: 'AAAAH♪', yodel: 'YODEL-AY-EE!', dopplerNooo: 'NOOOOO…',
};
